import { NavLink, useLocation } from 'react-router-dom';

/**
 * The Model Training pages for one programme (plan §7), in three groups:
 *   Manage    run the work -- the pipeline, and the programme's training project in the
 *             same Board, Backlog, Priorities (WSJF) and Gantt views every project uses
 *   Insights  read models of the programme's stores (FE-4 dashboards)
 *   Settings  how the programme is judged and run, and who reaches it
 */
type Tab = { path: string; label: string };
export const GROUPS: { key: string; label: string; tabs: Tab[] }[] = [
  { key: 'manage', label: 'Manage', tabs: [
    { path: '', label: 'Programme map' },
    { path: 'work/board', label: 'Board' },
    { path: 'work/list', label: 'Backlog' },
    { path: 'work/priorities', label: 'Priorities (WSJF)' },
    { path: 'work/gantt', label: 'Gantt' },
    { path: 'decisions', label: 'Decisions' },
    { path: 'relationships', label: 'Relationships' },
  ] },
  { key: 'insights', label: 'Insights', tabs: [
    { path: 'runs', label: 'Runs and spend' },
    { path: 'models', label: 'Models' },
    { path: 'atoms', label: 'Atoms' },
    { path: 'evidence', label: 'Evidence' },
    { path: 'registry', label: 'Registry and grounding' },
    { path: 'teachers', label: 'Teachers' },
    { path: 'funnel', label: 'Funnel' },
    { path: 'gates', label: 'Gates' },
    { path: 'ontology', label: 'Ontology' },
    { path: 'process-map', label: 'Process map' },
  ] },
  { key: 'settings', label: 'Settings', tabs: [
    { path: 'profile', label: 'Profile' },
    { path: 'access', label: 'Access' },
  ] },
];

/** Every tab, flat (the dashboard page looks up its label here). */
export const BOARDS: Tab[] = GROUPS.flatMap((g) => g.tabs);

export default function TrainingTabs({ programId }: { programId: string }) {
  const base = `/training/${encodeURIComponent(programId)}`;
  const { pathname } = useLocation();
  const rest = decodeURIComponent(pathname).slice(decodeURIComponent(base).length).replace(/^\//, '');
  const active = GROUPS.find((g) => g.tabs.some((t) => t.path && (rest === t.path || rest.startsWith(t.path + '/'))))
    || GROUPS[0];
  const link = (t: Tab) => (t.path ? `${base}/${t.path}` : base);
  return (
    <nav className="border-b border-gray-200 dark:border-gray-700" aria-label="Model Training pages">
      <div className="flex gap-4 text-xs uppercase tracking-wide mb-1">
        {GROUPS.map((g) => (
          <NavLink key={g.key} to={link(g.tabs[0])} end={!g.tabs[0].path}
            className={() => `pb-0.5 ${g.key === active.key ? 'text-accent font-semibold' : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}>
            {g.label}
          </NavLink>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {active.tabs.map((t) => (
          <NavLink key={t.path} end to={link(t)}
            className={({ isActive }) => `px-3 py-1.5 text-sm rounded-t-md -mb-px border-b-2 ${isActive
              ? 'border-accent text-accent font-medium'
              : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'}`}>
            {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
